/**
 * Device builds of a project's mobile services (Expo / React Native / Flutter), "Build for devices" in Apps and in the
 * Publish dialog: vfs-service, contract "Device builds — API contract (lane mobile)". Same base URL, authorized fetch
 * and ACL headers as the Quality tab (QualityService.js), and its errors: a DataError with `status` and the server's
 * `code` (e.g. 409 `connect_expo`, `connect_codemagic`, `no_signing`, `too_many`; 403 `not_in_plan`).
 * - the project's builds (its sandbox pod): `/api/v1/projects/:id/device-builds[...]`;
 * - the person's provider tokens (the gateway; values never come back): `/api/v1/me/device-builds/...`;
 * - a service's Android signing (the gateway; owner/editor; values never come back): `/api/v1/projects/:id/device-signing/:service`.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';
import { DataError, fail } from './DataService';
import { sameService } from '../utils/serviceTabsModel';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const seg = (value) => encodeURIComponent(String(value));
const projectUrl = (projectId, path = '') => `${base()}/api/v1/projects/${seg(projectId)}${path}`;
const buildsUrl = (projectId, path = '') => projectUrl(projectId, `/device-builds${path}`);
const meUrl = (path = '') => `${base()}/api/v1/me/device-builds${path}`;

/** The window event sent after a build starts (the lists read again at once). */
export const DEVICE_BUILDS_CHANGED_EVENT = 'flow:device-builds-changed';
export function notifyDeviceBuildsChanged(projectId) {
  if (typeof window !== 'undefined') { window.dispatchEvent(new CustomEvent(DEVICE_BUILDS_CHANGED_EVENT, { detail: { projectId } })); }
}

async function send(url, { method = 'GET', body, accept = 'application/json' } = {}) {
  if (!base()) { throw new DataError('vfs-service is not configured', 0, null); }
  const json = body !== undefined;
  return getAuthorizedFetch()(url, {
    method,
    headers: { Accept: accept, ...buildAclContextHeaders(), ...(json ? { 'Content-Type': 'application/json' } : {}) },
    ...(json ? { body: JSON.stringify(body) } : {}),
  });
}

async function call(url, options) {
  const response = await send(url, options);
  if (!response.ok) { throw await fail(response); }
  if (response.status === 204) { return {}; }
  return (await response.json().catch(() => null)) || {};
}

// ---- The project's builds ----

/** `{ available, apps, builds }`: whether builds can run here, the mobile services, the latest builds (newest first). */
export function getDeviceBuilds(projectId) {
  return call(buildsUrl(projectId));
}

/**
 * Starts a build: `{ service, platform, variant, provider, submit?, codemagic? }` -> `{ build }`. `submit` only for an
 * EAS iOS build, `codemagic` (`{ appId, workflowId, branch? }`) only for Codemagic.
 */
export function startDeviceBuild(projectId, { service, platform, variant, provider, submit = false, codemagic = null }) {
  const body = { service, platform, variant, provider };
  if (submit && provider === 'eas' && platform === 'ios') { body.submit = true; }
  if (provider === 'codemagic' && codemagic) { body.codemagic = codemagic; }
  return call(buildsUrl(projectId), { method: 'POST', body });
}

export function getDeviceBuild(projectId, buildId) {
  return call(buildsUrl(projectId, `/${seg(buildId)}`));
}

/** A piece of the log from `from`: `{ text, next, done, truncated }`. */
export function getDeviceBuildLog(projectId, buildId, from = 0) {
  return call(buildsUrl(projectId, `/${seg(buildId)}/log?from=${Number(from) || 0}`));
}

export function cancelDeviceBuild(projectId, buildId) {
  return call(buildsUrl(projectId, `/${seg(buildId)}/cancel`), { method: 'POST', body: {} });
}

export function deleteDeviceBuild(projectId, buildId) {
  return call(buildsUrl(projectId, `/${seg(buildId)}`), { method: 'DELETE' });
}

/** The URL of an artifact (the file, or a redirect to the provider's download). */
export const artifactUrl = (projectId, buildId, name) => buildsUrl(projectId, `/${seg(buildId)}/artifacts/${seg(name)}`);

/**
 * Downloads an artifact as a file (fetch and blob: the auth header). The server's file name, else `name`. A build
 * kept by the provider answers with a redirect the browser follows.
 */
export async function downloadArtifact(projectId, buildId, name) {
  const response = await send(artifactUrl(projectId, buildId, name), { accept: '*/*' });
  if (!response.ok) { throw await fail(response); }
  const blob = await response.blob();
  const header = response.headers?.get?.('Content-Disposition') || '';
  const match = /filename="?([^";]+)"?/i.exec(header);
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = match ? match[1] : name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10000);
}

/** A public link to the APK valid for 1 hour, for the QR code a phone scans: `{ url, expiresAt }`. */
export function createInstallLink(projectId, buildId, name) {
  return call(`${artifactUrl(projectId, buildId, name)}/install-link`, { method: 'POST', body: {} });
}

// ---- The person's provider tokens ----

/** `{ available, reason, providers: { expo, codemagic } }`, each `{ connected, account, updatedAt, help: { url, steps } }`. */
export function getConnections() {
  return call(meUrl('/connections'));
}

/** Saves a token (`expo` | `codemagic`): `{ provider: { connected, account } }`; 400 `invalid_token` when refused. */
export function connectProvider(provider, token) {
  return call(meUrl(`/connections/${seg(provider)}`), { method: 'PUT', body: { token } });
}

export function disconnectProvider(provider) {
  return call(meUrl(`/connections/${seg(provider)}`), { method: 'DELETE' });
}

/** The person's Codemagic apps and their workflows: `{ apps: [{ id, name, repository, workflows: [{ id, name }] }] }`. */
export function getCodemagicApps() {
  return call(meUrl('/codemagic/apps'));
}

// ---- A service's Android signing ----

/** `{ configured, keyAlias, updatedAt, updatedBy, available, reason }`. */
export function getDeviceSigning(projectId, service) {
  return call(projectUrl(projectId, `/device-signing/${seg(service)}`));
}

/** `{ keystore (base64), storePassword, keyAlias, keyPassword }` -> the same as GET. */
export function saveDeviceSigning(projectId, service, { keystore, storePassword, keyAlias, keyPassword }) {
  return call(projectUrl(projectId, `/device-signing/${seg(service)}`), {
    method: 'PUT', body: { keystore, storePassword, keyAlias, keyPassword },
  });
}

export function deleteDeviceSigning(projectId, service) {
  return call(projectUrl(projectId, `/device-signing/${seg(service)}`), { method: 'DELETE' });
}

// ---- Pure helpers the views share ----

/** A build still going on (queued or running). */
export const isActiveBuild = (build) => build?.status === 'queued' || build?.status === 'running';

/** The mobile app entry of a service (`apps` of GET device-builds; `api` or `services/api`), or null. */
export const mobileAppOf = (data, service) => (data?.apps || []).find((app) => sameService(app.service, service)) || null;
